import { Layer, Source } from 'react-map-gl';
import { Feature, FeatureCollection, LineString } from 'geojson';
import { COLOR_GREEN_700, COLOR_BACKGROUND_ACCENT } from '@alltrails/denali/tokens';
import { getChevronsId, getOutlineId, getPolylineId, polylinesTopId } from '../../utils/layers';
import ChevronLayer from './ChevronLayer';

type Props = {
  data: Feature<LineString> | FeatureCollection<LineString>;
  hide?: boolean;
  hideArrows?: boolean;
  lineColor?: string;
  lineOutlineColor?: string;
  lineProgress?: number;
  opacity?: number;
  uniqueId?: string;
  beforeUniqueId?: string;
  hasSourcePaint?: boolean;
};

export const getSourceId = (uniqueId?: string) => (uniqueId ? `polyline-source-${uniqueId}` : 'polyline-source');

const PolylineSource = ({
  data,
  hide,
  hideArrows = false,
  lineColor = COLOR_BACKGROUND_ACCENT,
  lineOutlineColor = COLOR_GREEN_700,
  lineProgress = 1,
  opacity = 1,
  uniqueId,
  beforeUniqueId,
  hasSourcePaint = false
}: Props) => {
  const outlineId = getOutlineId(uniqueId);
  const polylineId = getPolylineId(uniqueId);
  const chevronsId = getChevronsId(uniqueId);
  const sourceId = getSourceId(uniqueId);
  // Insert all layers before the bottom layer of another PolylineSource or sandwich this between polylinesBottomId and polylinesTopId unless
  const beforeId = beforeUniqueId ? getOutlineId(beforeUniqueId) : polylinesTopId;

  return (
    <Source
      id={sourceId}
      type="geojson"
      data={data}
      lineMetrics // Allows us to use line-progress below
    >
      {!hideArrows && lineProgress === 1 && (
        <ChevronLayer beforeId={beforeId} chevronLayerId={chevronsId} sourceId={sourceId} hide={hide} opacity={opacity} />
      )}
      <Layer
        beforeId={!hideArrows && lineProgress === 1 ? chevronsId : beforeId}
        id={polylineId}
        source={sourceId}
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round',
          visibility: hide ? 'none' : 'visible'
        }}
        paint={{
          'line-width': 4,
          'line-opacity': ['case', ['has', 'opacity'], ['get', 'opacity'], opacity],
          ...(hasSourcePaint
            ? { 'line-color': ['get', 'lineColor'] }
            : { 'line-gradient': ['step', ['line-progress'], lineColor, lineProgress, 'rgba(0, 0, 0, 0)'] })
        }}
      />
      {/* keep an eye on https://github.com/mapbox/mapbox-gl-js/issues/4162 to get rid of the need for another layer just for the outline */}
      <Layer
        beforeId={polylineId}
        id={outlineId}
        type="line"
        source={sourceId}
        layout={{
          'line-join': 'round',
          'line-cap': 'round',
          visibility: hide ? 'none' : 'visible'
        }}
        paint={{
          'line-width': 8,
          'line-opacity': ['case', ['has', 'opacity'], ['get', 'opacity'], opacity],
          ...(hasSourcePaint
            ? { 'line-color': ['get', 'lineOutlineColor'] }
            : { 'line-gradient': ['step', ['line-progress'], lineOutlineColor, lineProgress, 'rgba(0, 0, 0, 0)'] })
        }}
      />
    </Source>
  );
};

export default PolylineSource;
